import React, { Key, memo, ReactElement, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { Table } from 'antd'
import styled from 'styled-components'
import { v4 as uuidv4 } from 'uuid'

import { TemporaryDataType, TemporarySetDataType } from './useTemporaryDataState'
import { interventionColumns } from './static-data-columns'
import { EditableCell, EditableRow } from './editable-table-utils'
import TemporarySectionTable from './TemporarySectionTable'
import TemporaryRigTablesTable from './TemporaryRigTablesTable'
import { fetchEntityOptions } from '../../../widgets/static-data/api/api'
import { CustomDateTimePicker } from './components/CustomDateTimePicker'
import { emptyIntervention } from '../utils/emptyEntities'
import { CustomTableHeader } from './components/CustomTableHeader'
import { CustomExpandRowIcon } from '../temporary-data-table/components/CustomExpandRowIcon'
import { saveAllTemporaryData } from '../importer-api'
import { deleteEntityAndItsChildren, deletedCascadeEntities } from '../utils/actions'
import { StaticDataImporterContext } from '../context/StaticDataImporterContext'
import { getSelectedRowKeys, useSelectAllRows } from '../hooks/useSelectAllRows'
import { useNavigateToNewRow } from '../hooks/useNavigateToNewRow'
import { RowSelectionType } from 'antd/lib/table/interface'
import { useMoveEntityRowToAnotherEntityRowParent } from '../hooks/useMoveEntityRowToAnotherEntityRowParent'

type EditableTableProps = Parameters<typeof Table>[0]

type ColumnTypes = Exclude<EditableTableProps['columns'], undefined>

interface TemporaryInterventionTableProps {
    parent: WellDto
    data: TemporaryDataType
    setData: TemporarySetDataType
    localErrors: LocalErrorsTypes[]
    removeErrorWhenCheckedRowIsRemoved: (value: TemporaryDataType) => void
}

const TemporaryInterventionTable = memo(
    ({
        parent,
        data,
        setData,
        localErrors,
        removeErrorWhenCheckedRowIsRemoved
    }: TemporaryInterventionTableProps): ReactElement => {
        const [optionsOfInterventionEntity, setOptionsOfInterventionEntity] = useState(null)
        const [startDate, setStartDate] = useState<Record<number, Date | string>>(null)
        const [endDate, setEndDate] = useState<Record<number, Date | string>>(null)

        const {
            checkedRows,
            setCheckedRows,
            formatCheckedRows,
            setIsValidToTransfer,
            updateValueFromCheckedEntity,
            expandedRowKeys,
            setExpandedRowKeysByEntity
        } = useContext(StaticDataImporterContext)

        const { selectAllRows } = useSelectAllRows()

        const { goToNewRow, classNameNewRow } = useNavigateToNewRow()
        const { moveRowToAnotherParent } = useMoveEntityRowToAnotherEntityRowParent()

        const interventions = useMemo(
            () =>
                data.interventions
                    .filter(inter => inter.parent == parent.nodeId)
                    .map(intervention => ({
                        ...intervention,
                        key: String(intervention.rowId),
                        parent: parent.nodeId
                    })),
            [data.interventions, parent.nodeId]
        )

        useEffect(() => {
            fetchEntityOptions('intervention').then(data => setOptionsOfInterventionEntity(data))

            const newStartDate: Record<number, Date | string> = {}
            const newEndDate: Record<number, Date | string> = {}
            for (const intervention of data.interventions) {
                newStartDate[intervention.rowId] = intervention.start ? new Date(intervention.start) : ''
                newEndDate[intervention.rowId] = intervention.end ? new Date(intervention.end) : ''
            }
            setStartDate(newStartDate)
            setEndDate(newEndDate)
        }, []) // eslint-disable-line react-hooks/exhaustive-deps

        // Functions
        const handleEditData = useCallback(
            (entity: InterventionsDto[], row: InterventionsDto, setter: TemporarySetDataType): void => {
                const newData = [...entity]
                const index = newData.findIndex(item => String(row.rowId) === String(item.rowId))
                const item = newData[index]
                if (item) {
                    saveAllTemporaryData(row, 'INTERVENTION').then(saves => {
                        row.rowId = saves[0].id
                        row.key = saves[0].id
                        newData.splice(index, 1, {
                            ...item,
                            ...row
                        })
                        setter.setInterventions(newData)
                        setIsValidToTransfer(false)
                        updateValueFromCheckedEntity(`interventions=>${row.parent}`, row)
                    })
                }
            },
            [setIsValidToTransfer, updateValueFromCheckedEntity]
        )

        const handleEditSave = useCallback(
            (row: InterventionsDto): void => {
                handleEditData(data.interventions, row, setData)
            },
            [data.interventions, handleEditData, setData]
        )

        const setRowStartDate = useCallback(
            (rowId: number, newDate: Date): void => {
                // Changing entity row
                const newData = [...data.interventions]
                const currentRow = newData.find(row => String(row.rowId) === String(rowId))
                if (currentRow) {
                    currentRow.start = newDate ? new Date(newDate).getTime() : null
                    handleEditSave(currentRow)
                }
                // Changing startDate state
                const newStartDate = { ...startDate }
                newStartDate[rowId] = newDate
                setStartDate(newStartDate)
            },
            [data.interventions, handleEditSave, startDate]
        )

        const setRowEndDate = useCallback(
            (rowId: number, newDate: Date): void => {
                // Changing entity row
                const newData = [...data.interventions]
                const currentRow = newData.find(row => String(row.rowId) === String(rowId))
                if (currentRow) {
                    currentRow.end = newDate ? new Date(newDate).getTime() : null
                    handleEditSave(currentRow)
                }
                // Changing endDate state
                const newEndDate = { ...endDate }
                newEndDate[rowId] = newDate
                setEndDate(newEndDate)
            },
            [data.interventions, endDate, handleEditSave]
        )

        const handleAddNewRow = useCallback((): void => {
            const oldEntity = [...data.interventions]

            const newEntity: InterventionsDto = {
                ...emptyIntervention,
                key: undefined,
                parent: parent.nodeId,
                frontParentUuid: parent.frontUuid,
                nodeId: Date.now(),
                rowId: undefined,
                frontUuid: uuidv4()
            }

            saveAllTemporaryData(newEntity, 'INTERVENTION').then(saves => {
                newEntity.rowId = saves[0].id
                newEntity.key = saves[0].id
                newEntity.newRow = true
                oldEntity.push(newEntity)
                setData.setInterventions(oldEntity)
                goToNewRow()
            })
        }, [data.interventions, goToNewRow, parent.frontUuid, parent.nodeId, setData])

        const handleDelete = useCallback(
            (record: InterventionsDto): void => {
                const newData = data.interventions.filter(item => String(item.rowId) !== String(record.rowId))
                const newCheckedRows = deletedCascadeEntities(checkedRows, record)

                deleteEntityAndItsChildren(record.rowId).then(() => {
                    const newCheckedToTransfer = formatCheckedRows(newCheckedRows)

                    setData.setInterventions(newData)
                    setCheckedRows(newCheckedRows)
                    removeErrorWhenCheckedRowIsRemoved(newCheckedToTransfer)
                })
            },
            [
                checkedRows,
                data.interventions,
                formatCheckedRows,
                removeErrorWhenCheckedRowIsRemoved,
                setCheckedRows,
                setData
            ]
        )

        // Element components
        const handleCalendarElementStartDate = useCallback(
            (record: InterventionsDto): ReactElement => {
                if (!startDate) return null

                return (
                    <CustomDateTimePicker
                        date={startDate[record.rowId] as string}
                        setDate={setRowStartDate}
                        rowId={record.rowId}
                    />
                )
            },
            [setRowStartDate, startDate]
        )

        const handleCalendarElementEndDate = useCallback(
            (record: InterventionsDto): ReactElement => {
                if (!endDate) return null

                return (
                    <CustomDateTimePicker
                        date={endDate[record.rowId] as string}
                        setDate={setRowEndDate}
                        rowId={record.rowId}
                    />
                )
            },
            [endDate, setRowEndDate]
        )

        const components = useMemo(
            () => ({
                body: {
                    row: EditableRow,
                    cell: EditableCell
                }
            }),
            []
        )

        const interventionCols = useMemo(
            () =>
                interventionColumns(
                    handleDelete,
                    handleEditSave,
                    optionsOfInterventionEntity,
                    handleCalendarElementStartDate,
                    handleCalendarElementEndDate,
                    (record: EntityDto, selectedParent: number) =>
                        moveRowToAnotherParent(record, selectedParent, handleEditSave),
                    data.wells,
                    localErrors
                ),
            [
                data.wells,
                handleCalendarElementEndDate,
                handleCalendarElementStartDate,
                handleDelete,
                handleEditSave,
                localErrors,
                moveRowToAnotherParent,
                optionsOfInterventionEntity
            ]
        )

        const expandedRowRender = useCallback(
            (record: SectionDto): ReactElement => {
                return (
                    <>
                        <TemporarySectionTable
                            parent={(record as unknown) as SectionDto}
                            data={data}
                            setData={setData}
                            localErrors={localErrors}
                            removeErrorWhenCheckedRowIsRemoved={removeErrorWhenCheckedRowIsRemoved}
                        />
                        <TemporaryRigTablesTable
                            parent={(record as unknown) as InterventionsDto}
                            data={data}
                            setData={setData}
                            localErrors={localErrors}
                            removeErrorWhenCheckedRowIsRemoved={removeErrorWhenCheckedRowIsRemoved}
                        />
                    </>
                )
            },
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [localErrors, expandedRowKeys?.interventions, removeErrorWhenCheckedRowIsRemoved, setData]
        )

        const expandIcon = useCallback(({ expanded, onExpand, record }): ReactElement => {
            return <CustomExpandRowIcon expanded={expanded} onExpand={onExpand} record={record as InterventionsDto} />
        }, [])

        const title = useCallback((): ReactElement => {
            return <CustomTableHeader title="interventionKey" handleAddNewRow={handleAddNewRow} />
        }, [handleAddNewRow])

        const rowSelection = useMemo(
            () => ({
                selectedRowKeys: getSelectedRowKeys(checkedRows, 'interventions', parent),
                type: 'checkbox' as RowSelectionType,
                onChange: (selectedRowsKey: React.Key[], selectedRows: InterventionsDto[]) => {
                    selectAllRows({
                        data,
                        selectedRows,
                        parentNodeId: parent.nodeId,
                        type: 'interventions'
                    })
                },
                onSelect: (
                    record: EntityDto,
                    selected: boolean,
                    selectedRows: InterventionsDto[],
                    nativeEvent: Event
                ) => {
                    selectAllRows({
                        data,
                        parentNodeId: parent.nodeId,
                        type: 'interventions',
                        selectedRows,
                        target: record,
                        selected,
                        nativeEvent
                    })
                }
            }),
            [checkedRows, data, parent, selectAllRows]
        )

        const handleExpandedRowsChange = useCallback(
            (keys: React.Key[]): void => {
                setExpandedRowKeysByEntity('interventions', keys as Key[])
            },
            [setExpandedRowKeysByEntity]
        )

        const expandable = useMemo(
            () => ({
                expandedRowRender,
                expandIcon,
                expandedRowKeys: expandedRowKeys?.interventions,
                onExpandedRowsChange: handleExpandedRowsChange,
                defaultExpandAllRows: false
            }),
            [expandIcon, expandedRowKeys?.interventions, expandedRowRender, handleExpandedRowsChange]
        )

        return (
            <TableContainer>
                <Table
                    title={title}
                    size="small"
                    components={components}
                    rowClassName={(d: EntityDto) => (d?.newRow ? `editable-row ${classNameNewRow}` : 'editable-row')}
                    bordered
                    dataSource={interventions}
                    columns={interventionCols as ColumnTypes}
                    pagination={false}
                    rowSelection={rowSelection}
                    expandable={expandable}
                />
            </TableContainer>
        )
    }
)

TemporaryInterventionTable.displayName = 'TemporaryInterventionTable'

export default TemporaryInterventionTable

const TableContainer = styled.main``
